import { ListService } from '@abp/ng.core';
import { Component, Injector, OnInit, Type } from '@angular/core';
import { XPgListComponent } from 'source/app/xui/x-page/x-pg-list/x-pg-list.component';
import { PagedRequestDto } from 'source/app/xui/x-page/x-pg-model/x-pg.model';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TypeOfCallSopInputPrecedenceRulesService } from 'source/app/proxy/type-of-calls';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-type-of-call-sop-input-precedence-rule',
  templateUrl: './type-of-call-sop-input-precedence-rule.component.html',
  styleUrls: ['./type-of-call-sop-input-precedence-rule.component.scss'],
})
export class TypeOfCallSopInputPrecedenceRuleComponent implements OnInit {

  searchValue = '';
  showColumnFilters = false;
  columnFilters: { [key: string]: string } = {};
  isEditMode = false;
  isVisible = false;
  isOkLoading = false;
  
  data: any[] = [];
  filteredData: any[] = []; 
  
  tableHeight = 535;
  typeOfCallId: string;
  
  sopInputs = [];
  precedenceForm: FormGroup;
  selectedPrecedence: any = null;
  selectedSuperseded: any = null;
  selectedSuperseding: any = null;


  component: Type<any>;
  injector: Injector;
  list: ListService<PagedRequestDto>;

  constructor(
    private inj: Injector,
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private nzMessageService: NzMessageService,
    private typeOfCallSopInputPrecedenceRulesService: TypeOfCallSopInputPrecedenceRulesService
  ) {
    this.typeOfCallId = this.route.parent?.snapshot.params.id;
  }

  ngOnInit(): void {
    this.updateTableHeight();
    this.initializeForm();
    this.getPrecedence();
    this.getSopInputs();
  }

  initializeForm() {
    this.precedenceForm = this.formBuilder.group({
      typeOfCallId: [this.typeOfCallId],
      supersededSopInputId: [null, Validators.required],
      supersedingSopInputId: [null, Validators.required],
    });
  }

  // Open modal for creating new precedence
  openmodal(): void {
    this.isEditMode = false;
    this.selectedPrecedence = null;
    this.precedenceForm.reset({
      typeOfCallId: this.typeOfCallId,
      supersededSopInputId: null,
      supersedingSopInputId: null,
    });
    this.isVisible = true;
  }

  onSearch(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase().trim();
    this.searchValue = searchTerm;
    this.applyFilters();
  }

  toggleColumnFilters(): void {
    this.showColumnFilters = !this.showColumnFilters;
    if (!this.showColumnFilters) {
      this.columnFilters = {};
      this.applyFilters();
    }
  }

  updateColumnFilter(column: string, event: Event): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase().trim();

    if (value) {
      this.columnFilters[column] = value;
    } else {
      delete this.columnFilters[column];
    }

    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.data];

    // Apply global search
    if (this.searchValue) {
      filtered = filtered.filter(item =>
        (item.superseded?.toLowerCase() || '').includes(this.searchValue) ||
        (item.superseding?.toLowerCase() || '').includes(this.searchValue)
      );
    }

    // Apply column filters
    Object.keys(this.columnFilters).forEach(column => {
      const filterValue = this.columnFilters[column];

      switch (column) {
        case 'superseded':
          filtered = filtered.filter(item =>
            (item.superseded?.toLowerCase() || '').includes(filterValue)
          );
          break;
        case 'superseding':
          filtered = filtered.filter(item =>
            (item.superseding?.toLowerCase() || '').includes(filterValue)
          );
          break;
      }
    });

    this.filteredData = filtered;
  }

  getPrecedence() {
    this.typeOfCallSopInputPrecedenceRulesService.getList({
      filter: `typeOfCallId eq "${this.typeOfCallId}"`,
      ignoreMaxResultCount: true,
    } as PagedRequestDto).subscribe(response => {
      this.data = response.items.map(x => ({
        id: x.id,
        superseded: x.supersededSopInputTitle,
        superseding: x.supersedingSopInputTitle,
        supersededSopInputId: x.supersededSopInput,
        supersedingSopInputId: x.supersedingSopInput,
        concurrencyStamp: x.concurrencyStamp
      }));
      this.filteredData = [...this.data];
    });
  }

  getSopInputs() {
    // Fetch SOP inputs for the dropdown - filter by typeOfCallId if needed
    this.typeOfCallSopInputPrecedenceRulesService.getSopInputs(this.typeOfCallId).subscribe(response => {
      this.sopInputs = response;
    }, error => {
      console.error('Error fetching SOP inputs:', error);
      // Fallback: If the method doesn't exist, you might need to use a different service
      // this.sopInputService.getList({...}).subscribe(...)
    });
  }

  onSupersededChange(id: string) {
  this.selectedSuperseded = this.sopInputs.find(x => x.id === id) || null;
}

onSupersedingChange(id: string) {
  this.selectedSuperseding = this.sopInputs.find(x => x.id === id) || null;
}


  editPrecedence(id: string): void {
  const precedence = this.data.find(x => x.id === id);

  if (!precedence) return;

  this.isEditMode = true;
  this.selectedPrecedence = precedence;

  // PATCH FORM - THIS MAKES DROPDOWN AUTO SELECT
  this.precedenceForm.patchValue({
    typeOfCallId: this.typeOfCallId,
    supersededSopInputId: precedence.supersededSopInputId.id,
    supersedingSopInputId: precedence.supersedingSopInputId.id,
  });


  // Optional: Keep selected objects if you want titles
  this.selectedSuperseded = this.sopInputs.find(x => x.id === precedence.supersededSopInputId);
  this.selectedSuperseding = this.sopInputs.find(x => x.id === precedence.supersedingSopInputId);

  this.isVisible = true;
}


  savePrecedence(): void {
    if (this.precedenceForm.invalid) {
      this.nzMessageService.error('Please fill all required fields!');
      return;
    }

    // Validate that superseded and superseding are not the same
    const formValues = this.precedenceForm.value;
    if (formValues.supersededSopInputId === formValues.supersedingSopInputId) {
      this.nzMessageService.error('Cannot create precedence for the same SOP Input');
      return;
    }

    this.isOkLoading = true;
    const input = this.precedenceForm.value;

    this.typeOfCallSopInputPrecedenceRulesService.create(input).subscribe({
      next: () => {
        this.nzMessageService.success('Precedence rule added successfully!');
        this.isVisible = false;
        this.isOkLoading = false;
        this.getPrecedence();
      },
      error: (error) => {
        this.isOkLoading = false;
        const backendMessage = error?.error?.error?.message || 'Failed to add precedence rule';
        this.nzMessageService.error(backendMessage);
      },
    });
  }

  updatePrecedence(): void {
    if (this.precedenceForm.valid && this.selectedPrecedence) {
      // Validate that superseded and superseding are not the same
      const formValues = this.precedenceForm.value;
      if (formValues.supersededSopInputId === formValues.supersedingSopInputId) {
        this.nzMessageService.error('Cannot create precedence for the same SOP Input');
        return;
      }

      this.isOkLoading = true;

      const updateDto = {
        typeOfCallId: formValues.typeOfCallId,
        supersededSopInputId: formValues.supersededSopInputId,
        supersedingSopInputId: formValues.supersedingSopInputId,
        concurrencyStamp: this.selectedPrecedence.concurrencyStamp,
      };

      this.typeOfCallSopInputPrecedenceRulesService
        .update(this.selectedPrecedence.id, updateDto)
        .subscribe({
          next: (response) => {
            const index = this.data.findIndex(item => item.id === response.id);
            if (index !== -1) {
              this.data[index] = {
                id: response.id,
                superseded: response.supersededSopInputTitle,
                superseding: response.supersedingSopInputTitle,
                supersededSopInputId: response.supersededSopInput,
                supersedingSopInputId: response.supersedingSopInput,
                concurrencyStamp: response.concurrencyStamp
              };
              this.applyFilters();
            }
            this.nzMessageService.success('Precedence rule updated successfully.');
            this.isVisible = false;
            this.isOkLoading = false;
          },
          error: (error) => {
            this.isOkLoading = false;
            const backendMessage = error?.error?.error?.message || 'Failed to update precedence rule';
            this.nzMessageService.error(backendMessage);
            console.error(`Failed to update: ${error.status} ${error.statusText}`);
          },
        });
    } else {
      this.nzMessageService.error('Please fill all required fields!');
    }
  }

  deletePrecedence(id: string): void {
    if (!id) {
      this.nzMessageService.error('Invalid precedence rule ID');
      return;
    }

    this.typeOfCallSopInputPrecedenceRulesService.delete(id).subscribe({
      next: () => {
        this.data = this.data.filter(item => item.id !== id);
        this.applyFilters();
        this.nzMessageService.success('Precedence rule deleted successfully');
      },
      error: (error) => {
        this.nzMessageService.error('Failed to delete precedence rule');
        console.error('Error deleting precedence rule:', error);
      },
    });
  }

  handleSave(): void {
    if (this.isEditMode && this.selectedPrecedence) {
      this.updatePrecedence();
    } else {
      this.savePrecedence();
    }
  }

  handleCancel(): void {
    this.isVisible = false;
    this.isEditMode = false;
    this.selectedPrecedence = null;
    this.isOkLoading = false;
    this.precedenceForm.reset({
      typeOfCallId: this.typeOfCallId,
      supersededSopInputId: null,
      supersedingSopInputId: null,
    });
  }

  get isSaveDisabled(): boolean {
    return this.precedenceForm.invalid || this.isOkLoading;
  }

  updateTableHeight() {
    const screenHeight = window.innerHeight;

    if (screenHeight < 1080) {
      this.tableHeight = 535;
    } else if (screenHeight > 1080) {
      this.tableHeight = 750;
    }
  }


  sortSuperseded = (a: any, b: any) => (a.superseded || '').localeCompare(b.superseded || '');
  sortSuperseding = (a: any, b: any) => (a.superseding || '').localeCompare(b.superseding || '');
}